/**
 * Robust klient för contact-chat Edge-funktion: retry, backoff och vänliga fel.
 */

const DEFAULT_RETRIES = 2;
const BASE_DELAY_MS = 350;

export async function contactChatFetchWithRetry(
  url: string,
  init: RequestInit,
  options?: { retries?: number; baseDelayMs?: number }
): Promise<Response> {
  const max = options?.retries ?? DEFAULT_RETRIES;
  const baseDelay = options?.baseDelayMs ?? BASE_DELAY_MS;
  let lastErr: unknown;
  let lastRes: Response | undefined;

  for (let attempt = 0; attempt <= max; attempt++) {
    try {
      const res = await fetch(url, init);
      lastRes = res;
      if (res.ok) return res;

      const retryable =
        res.status === 429 || (res.status >= 500 && res.status < 600) || res.status === 408;
      if (retryable && attempt < max) {
        await delay(baseDelay * (attempt + 1));
        continue;
      }
      return res;
    } catch (e) {
      lastErr = e;
      if (attempt < max) {
        await delay(baseDelay * (attempt + 1));
        continue;
      }
    }
  }

  if (lastRes) return lastRes;
  throw lastErr instanceof Error ? lastErr : new Error('network');
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export type ParsedContactError = {
  status: number;
  message: string;
  /** Ogiltig session – rensa localStorage och börja om */
  resetSession: boolean;
};

export async function parseContactChatErrorResponse(res: Response): Promise<ParsedContactError> {
  let bodyText = '';
  try {
    bodyText = await res.text();
  } catch {
    bodyText = '';
  }
  let errMsg = '';
  try {
    const j = JSON.parse(bodyText) as { error?: string };
    errMsg = (j.error ?? '').trim();
  } catch {
    errMsg = bodyText.trim();
  }

  if (res.status === 400) {
    const low = errMsg.toLowerCase();
    const captcha =
      low.includes('recaptcha') || low.includes('captcha') || low.includes('verifier');
    return {
      status: res.status,
      message: captcha
        ? 'Säkerhetskontrollen misslyckades. Uppdatera sidan och försök igen.'
        : errMsg || 'Något gick inte att skicka. Kontrollera och försök igen.',
      resetSession: false,
    };
  }

  if (res.status === 401) {
    return {
      status: res.status,
      message: 'Anslutningen avbröts. Ladda om sidan och försök igen.',
      resetSession: false,
    };
  }

  if (res.status === 429) {
    return {
      status: res.status,
      message: 'Det är lite hög belastning just nu. Vänta en stund och försök igen.',
      resetSession: false,
    };
  }

  if (res.status === 403) {
    const invalidSession =
      errMsg.includes('Ogiltig') ||
      errMsg.includes('konversation') ||
      errMsg.toLowerCase().includes('invalid');
    if (invalidSession) {
      return {
        status: res.status,
        message:
          'Vi kunde inte hitta din chatt-session. Fyll i formuläret igen så når du oss.',
        resetSession: true,
      };
    }
    return {
      status: res.status,
      message:
        'Det gick inte att ansluta till chatten just nu. Ladda om sidan om problemet kvarstår.',
      resetSession: false,
    };
  }

  if (res.status >= 500) {
    return {
      status: res.status,
      message: 'Servern svarar inte tillfälligt. Försök igen om en liten stund.',
      resetSession: false,
    };
  }

  return {
    status: res.status,
    message: errMsg || 'Något gick fel. Försök igen.',
    resetSession: false,
  };
}

export function friendlyNetworkError(err: unknown): string {
  if (!(err instanceof Error)) {
    return 'Kunde inte ansluta. Kontrollera nätverket och försök igen.';
  }
  const m = err.message.toLowerCase();
  if (m === 'network' || m.includes('fetch') || m.includes('network') || m.includes('failed')) {
    return 'Ingen eller bruten anslutning. Försök igen om ett ögonblick.';
  }
  return err.message;
}
